import { createContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { RootState } from 'src/store/store';
import clsx from 'clsx';

import WrapperSummary from '@components/styled-UI/WrapperSummary';
import WrapperSummaryRetail from '@components/styled-UI/WrapperSummaryRetail';

import { IOnlineOrderHistory, IRetailOrderHistory } from '@interfaces/myPurchasesInterfaces';
import { GenericSelectOptions } from '@interfaces/mainInterfaces';

import { getLabelFallback } from '@utils/utils';

import { cloneDeep } from 'lodash';

import OnlineOrderButton from '@images/svg-icons/onlineOrderButton.svg'
import RetailOrderButton from '@images/svg-icons/retailOrderButton.svg'

interface Props {
    onlineOrders?: IOnlineOrderHistory[];
    retailOrders?: IRetailOrderHistory[];
}

const monthsFiltersOptions: GenericSelectOptions[] = [
    {
        name: getLabelFallback('MY_PURCHASES_ONLINE_ORDERS_FIRST_SELECT_OPTION'),
        value: 6,
    },
];

const OnlineOrderHistoryOverviewContext = createContext(null);

const OnlineOrderHistoryOverview = ({ onlineOrders, retailOrders }: Props): JSX.Element => {
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const config = useSelector((state: RootState) => state.common.config);
    const encodedOrderId = useSelector((state: RootState) => state.myPurchases.encodedOrderId);

    const [monthsFilter, setMonthsFilter] = useState<GenericSelectOptions[]>(monthsFiltersOptions);
    const [currentFilter, setCurrentFilter] = useState<string>(monthsFiltersOptions[0].value);
    const [onlineOrderHistory, setOnlineOrderHistory] =
        useState<IOnlineOrderHistory[]>(onlineOrders);

    const navigate = useNavigate();

    const dispatch = useDispatch();

    const orderHistoryLength = !!onlineOrders ? onlineOrders?.length : 0;
    const retailHistoryLength = !!retailOrders ? retailOrders?.length : 0;
    const [retailOrdersSelected, setRetailOrdersSelected] = useState(false);

    useEffect(() => {
        if (onlineOrders?.length) {
            addMonthsFilterOptions(onlineOrderHistory);
        } else {
            setRetailOrdersSelected(true)
        }
    }, [onlineOrders]);

    useEffect(() => {
        if (onlineOrders?.length) {
            filterOrdersHistory();
        }
    }, [currentFilter]);

    /** The function below returns the online order history filtered list based on the select option */

    const filterOrdersHistory = () => {
        const filteredOrders = onlineOrders.filter(order => {
            return Number(currentFilter) === 6
                ? new Date(order.placedDate).getMonth() + 1 >=
                      Number(new Date().getMonth() + 1) - Number(currentFilter) &&
                      new Date(order.placedDate).getFullYear() === new Date().getFullYear()
                : new Date(order.placedDate).getFullYear() === Number(currentFilter);
        });
        setOnlineOrderHistory(filteredOrders);
    };

    /** The function below adds new filter options to the select component according to the orders dates */

    const addMonthsFilterOptions = (onlineOrders: IOnlineOrderHistory[]) => {
        let monthsSelectOptions = cloneDeep(monthsFilter);
        const orderDates = onlineOrders.map(
            ({
                orderId,
                orderAddressEmail,
                orderStatus,
                grandTotal,
                grandTotalCurrency,
                isPrescriptionRequired,
                ...rest
            }) => new Date(rest.placedDate).getFullYear()
        );
        Array.from(new Set(orderDates)).map(element => {
            monthsSelectOptions.push({ name: element.toString(), value: element });
        });

        setMonthsFilter(monthsSelectOptions);
    };

    return (
        <div className="online-order-history-overview">
            <div
                role="region"
                aria-label="Online order history overview header"
                className="online-orders-history-overview-header"
            >
                <div className="title-container">
                    <div className="title">
                        {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_PAGE_LABEL')}
                    </div>
                </div>
                {orderHistoryLength > 0 && retailHistoryLength > 0 && (
                    <div className="online-orders-history-overview-header-container-button" role="navigation" aria-label="filter orders type">
                        <div className="online-orders-history-overview-header-container-button-online">
                            <button
                                className={clsx(
                                    'primary-button button-size-medium toggle online',
                                    retailOrdersSelected && 'toggle-not-selected',
                                    !retailOrdersSelected && 'toggle-selected black'
                                )}
                                onClick={() => setRetailOrdersSelected(false)}
                                aria-label="show online orders"
                            >
                                <OnlineOrderButton />
                                {getLabelFallback(
                                    'MYPURCHASES_ONLINE_ORDERS_PAGE_LABEL_BUTTON_ONLINE'
                                )} 
                            </button>
                        </div>
                        <div className="online-orders-history-overview-header-container-button-retail">
                            <button
                                className={clsx(
                                    'primary-button button-size-medium toggle retail',
                                    retailOrdersSelected && 'toggle-selected black',
                                    !retailOrdersSelected && 'toggle-not-selected'
                                )}
                                onClick={() => setRetailOrdersSelected(true)}
                                aria-label="show retail orders"
                            >
                                <RetailOrderButton />
                                {getLabelFallback(
                                    'MYPURCHASES_RETAIL_ORDERS_PAGE_LABEL_BUTTON_IN_STORE'
                                )}
                            </button>
                        </div>
                    </div>
                )}
                {onlineOrders && !retailOrdersSelected && (
                    <>
                        <div className="number-of-orders-container">
                            <div className='orders-quantity'>
                                {orderHistoryLength} orders
                            </div>
                        </div>
                    </>
                )}
                {retailOrders && retailOrdersSelected && (
                    <>
                        <div className="number-of-orders-container">
                            <div className='orders-quantity'>
                                {retailHistoryLength} orders
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className="online-orders-history-overview-content">
                {(orderHistoryLength > 0 && !retailOrdersSelected) && (
                    <>
                        {onlineOrders.map(order => (
                            <WrapperSummary key={order.orderId} order={order} />
                         ))}
                    </>
                )}

                {(retailHistoryLength > 0 && retailOrdersSelected) && (
                    <>
                        {retailOrders.map(order => (
                            <WrapperSummaryRetail key={order.orderId} order={order} />
                        ))}
                    </>
                )}
            </div>
        </div>
    );
};
export default OnlineOrderHistoryOverview;
